import { PlusIcon } from '@heroicons/react/24/outline';
import { useAppDispatch } from 'app/hooks';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import ExistingEquipmentTypeDiagram from 'components/structure-page/existing-equipment-type-diagrams';
import { ExistingSystemType } from 'data/types';
import { openEquipmentModal } from 'features/ui';
import {
  EXISTING_EQUIPMENT_ADD_BUTTONS,
  existingEquipmentAddButtonDisabledReason
} from 'utils/existing-equipment-add-buttons';

type ExistingEquipmentListProps = {
  system?: ExistingSystemType | null;
};

export default function ExistingEquipmentList({
  system = null
}: ExistingEquipmentListProps) {
  const dispatch = useAppDispatch();

  return (
    <div
      data-testid='existing-equipment-list'
      data-system-unique-id={system?.uniqueId}
      className='flex flex-col gap-3'
    >
      <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
        {EXISTING_EQUIPMENT_ADD_BUTTONS.map((button) => {
          const disabledReason = existingEquipmentAddButtonDisabledReason(
            system,
            button.kind
          );
          const disabled = disabledReason !== null;
          const handleClick = () => {
            if (disabled) return;
            dispatch(
              openEquipmentModal({
                kind: button.kind,
                targetSystemUniqueId: system?.uniqueId
              })
            );
          };
          return (
            <button
              key={button.kind}
              type='button'
              aria-disabled={disabled || undefined}
              onClick={handleClick}
              className={clsx(
                'relative flex min-h-[220px] flex-col items-center justify-between rounded-xl border-2 border-gray-200 bg-white px-4 py-5 text-center transition',
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:border-blue-400 hover:shadow-md'
              )}
            >
              <ExistingEquipmentTypeDiagram
                kind={button.kind}
                className='h-24 w-24 text-gray-600'
              />
              <div className='flex flex-col items-center gap-1'>
                <span className='flex items-center gap-1 text-base font-semibold text-gray-900'>
                  <PlusIcon className='h-4 w-4 shrink-0' />
                  {button.label}
                </span>
                {button.description && (
                  <span className='text-sm leading-snug text-gray-500'>
                    {button.description}
                  </span>
                )}
              </div>
              {disabledReason && <Tooltip text={disabledReason} />}
            </button>
          );
        })}
      </div>
      <p className='text-sm text-gray-500'>
        Click an item to add it, then fill in its details.
      </p>
    </div>
  );
}
